import React, { useCallback, useContext, useEffect, useState } from "react";
import { getDuplicateAccountCount, getDuplicateInvitationCount } from "../clients/DPLClient";
import { useAuth } from "./AuthContext";

type NavigationInfoContextType = {
  numDuplicateAccounts: number;
  numDuplicateInvitations: number;
  refreshAccountCount: () => void;
  refreshInvitationCount: () => void;
};

const NavigationInfoContext = React.createContext<NavigationInfoContextType | undefined>(undefined);

const NavigationInfoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isBocAdmin } = useAuth();
  const [numDuplicateAccounts, setNumDuplicateAccounts] = useState(0);
  const [numDuplicateInvitations, setNumDuplicateInvitations] = useState(0);

  const refreshAccountCount = useCallback(async () => {
    const count = await getDuplicateAccountCount();
    setNumDuplicateAccounts(count);
  }, []);

  const refreshInvitationCount = useCallback(async () => {
    const count = await getDuplicateInvitationCount();
    setNumDuplicateInvitations(count);
  }, []);

  useEffect(() => {
    if (isBocAdmin && refreshAccountCount && refreshInvitationCount) {
      refreshAccountCount();
      refreshInvitationCount();
    }
  }, [isBocAdmin, refreshAccountCount, refreshInvitationCount]);

  return (
    <NavigationInfoContext.Provider
      value={{ numDuplicateAccounts, numDuplicateInvitations, refreshAccountCount, refreshInvitationCount }}
    >
      {children}
    </NavigationInfoContext.Provider>
  );
};

const useNavigationInfo = (): NavigationInfoContextType => {
  const context: NavigationInfoContextType | undefined = useContext(NavigationInfoContext);
  if (context === undefined) {
    throw new Error(`useNavigationInfo must be used within a NavigationInfoProvider`);
  }
  return context;
};

export { NavigationInfoProvider, useNavigationInfo };
